import {useMemo} from "react"
import {QueryHookOptions,UseQueriesResult, useQuerys} from "@irati/utils/services/apollo"
import {PaymentCurrencyType, WalletEntityType} from "@irati/types"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {useWalletQuery} from "./wallet"

export const walletStates = {
  RP: "RP",
  FP: "FP",
  Blend: "Blend",
  None: "None"
} as const

/**
 * State of a blended user wallet where they either have a single currency or a
 * blend of both.
 *
 * The conditions for a `blend` state also takes into account balance and
 * business feature flags
 */
type WalletState = keyof typeof walletStates

interface WalletInfo {
  visible: boolean
  amount: number
  currency: PaymentCurrencyType
}

type PointsBlend = UseQueriesResult<{
  userWalletState: WalletState
  walletBlend: WalletInfo
  walletRP: WalletInfo
  walletFP: WalletInfo
}>

/**
 * Fetch a users blend points amount, which may be `undefined` if the user does not have access to both wallets
 */
export const usePointsBlendQuery = ({fetchPolicy}: Pick<QueryHookOptions, "fetchPolicy"> = {}): PointsBlend => {
  const userId = useAuth()?.user?.uuid ?? ""
  const hasFlexiWallet = !!useAuth()?.productPermissions?.wallet?.treatTokens
  const hasRewardsWallet = !!useAuth()?.productPermissions?.wallet?.flexiRewards
  const isPointsBlendEligible = !!useAuth()?.productPermissions?.wallet?.pointsBlendEligible

  const apollo = useQuerys(
    useWalletQuery(
      {userId, currency: PaymentCurrencyType.Tt, entityType: WalletEntityType.User, entityId: userId},
      {fetchPolicy, skip: !hasFlexiWallet}
    ),
    useWalletQuery(
      {userId, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.User, entityId: userId},
      {fetchPolicy, skip: !hasRewardsWallet}
    )
  )
  return useMemo(() => {
    const {data: [userFlexiWallet, userRewardWallet] = [], ...rest} = apollo
    const flexiAmount = userFlexiWallet?.amount ?? 0
    const rewardAmount = userRewardWallet?.amount ?? 0
    const userBlendBalance = flexiAmount + rewardAmount

    const userWalletState = getWalletState({
      flexiAmount,
      rewardAmount,
      hasRewardsWallet,
      hasFlexiWallet
    })

    return {
      ...rest,
      data: rest.loading
        ? undefined
        : {
            userWalletState,
            walletBlend: {
              visible: isPointsBlendEligible && userWalletState === "Blend",
              amount: isPointsBlendEligible && userWalletState === "Blend" ? userBlendBalance : 0,
              currency: PaymentCurrencyType.Blend
            },
            walletRP: {
              visible: [walletStates.RP, walletStates.Blend].includes(userWalletState),
              amount: rewardAmount,
              currency: PaymentCurrencyType.Rp
            },
            walletFP: {
              visible: [walletStates.FP, walletStates.Blend].includes(userWalletState),
              amount: flexiAmount,
              currency: PaymentCurrencyType.Tt
            }
          }
    }
  }, [apollo, hasFlexiWallet, hasRewardsWallet, isPointsBlendEligible])
}

export function getWalletState({
  hasFlexiWallet,
  hasRewardsWallet,
  flexiAmount,
  rewardAmount
}: {
  hasFlexiWallet: boolean
  hasRewardsWallet: boolean
  flexiAmount: number
  rewardAmount: number
}): WalletState {
  const availableFlexiWallet = hasFlexiWallet || !!flexiAmount
  const availableRewardWallet = hasRewardsWallet || !!rewardAmount

  const hasBlend =
    (hasRewardsWallet && hasFlexiWallet) ||
    (flexiAmount && hasRewardsWallet) ||
    (rewardAmount && hasFlexiWallet) ||
    (flexiAmount && rewardAmount)

  if (hasBlend) return "Blend"
  if (availableRewardWallet) return "RP"
  if (availableFlexiWallet) return "FP"
  return "None"
}
